// The shared browse-table pieces for Momentum's multi-instance tools.
//
// Extracted from MomentumHub when the Social Generator became the third consumer
// (after the Offer Ladder and the CTA Library). The pattern was already reused
// twice inside MomentumHub via module-private constants; a third inline copy in a
// different file is how this turns into Frankenstein.
//
// These reference canvas CSS variables DIRECTLY rather than taking a token object,
// because MomentumHub and SocialGenerator each keep their own `B` map over the
// same variables. Passing either one in would tie this file to whichever component
// happened to import it first.
//
// Deliberately NOT a single generic <BrowseTable> that renders rows for you: the
// Offer Ladder groups rows by rung with a separate Retired section, and saved
// social posts group by date. One component covering both ends up more tangled
// than these small parts. Columns and row rendering stay with the caller.

import { Children, Fragment, isValidElement, useEffect, useState } from "react";
import { GripVertical } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToHorizontalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/* ── Dragging rows into order ───────────────────────────────────────────────
   dnd-kit rather than the browser's own drag-and-drop, which the design
   prototype uses. Native HTML5 drag does not fire on touch AT ALL, so the grip
   would sit there looking draggable and do nothing on a phone — and this app has
   a mobile mode. dnd-kit also gives a keyboard path, which matters for an
   audience where dragging precisely is not a given.

   Shared here rather than written per table: Website sections first, then Course
   modules, and the two are meant to mirror each other.

   The grip is the ONLY drag surface. Making the whole row draggable would fight
   scrolling on touch and would swallow the row's own clicks. */

const HANDLE_STYLE = {
  display: "flex", alignItems: "center", justifyContent: "center",
  width: 16, height: 20, padding: 0, border: "none", background: "none",
  color: "var(--text-muted)", flexShrink: 0, touchAction: "none",
};

/**
 * A vertical sortable list.
 *
 * @param ids        the item ids, in current display order
 * @param onReorder  (fromIndex, toIndex) => void — called once, on drop
 * @param children   the rows, in the same order as `ids`
 * @param axis       "y" for a list, "x" for a strip. A funnel's steps read left
 *                   to right as a path, and a drag locked to the wrong axis
 *                   feels broken rather than restricted.
 */
export function SortableList({ ids, onReorder, children, axis = "y" }) {
  const horizontal = axis === "x";
  const sensors = useSensors(
    /* A small distance before a drag starts, so a tap on the grip can still be
       a tap and doesn't turn every touch into a drag. */
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(active.id);
    const to = ids.indexOf(over.id);
    if (from < 0 || to < 0) return;
    onReorder(from, to);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[horizontal ? restrictToHorizontalAxis : restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={horizontal ? horizontalListSortingStrategy : verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

/**
 * One sortable row. `children` is a function receiving `{ handle, dragging }` —
 * a render prop rather than a wrapper, because the handle has to be placed
 * inside the row's own header, not around it.
 */
export function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });

  const handle = (
    <button
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
      title="Drag to reorder"
      aria-label="Drag to reorder"
      style={{ ...HANDLE_STYLE, cursor: isDragging ? "grabbing" : "grab" }}
    >
      <GripVertical size={14} />
    </button>
  );

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        /* Lifted clear of its neighbours' borders while it moves. */
        zIndex: isDragging ? 2 : undefined,
        position: "relative",
      }}
    >
      {children({ handle, dragging: isDragging })}
    </div>
  );
}

/* ── Narrow screens ─────────────────────────────────────────────────────────
   A six-column grid does not squeeze; it collides. On a phone the Offer Ladder
   was rendering the type pill on top of the offer name and truncating summaries
   to "Get a fu…" — the columns kept their proportions all the way down, because
   nothing ever told them to stop being columns.

   So below 640px a row stops being a grid and becomes a small card: the name (and
   the actions button) on the first line, everything else as a wrapped meta line
   underneath. The header row hides itself, since column headings mean nothing
   once there are no columns.

   Done here rather than per table so the four Momentum tables can't drift, and
   done in JS rather than a media query because every element in this app is
   styled inline, and an inline style cannot be targeted by one. There IS real
   CSS — App.jsx renders the generated token sheet and the resets into <style>
   tags — so a media query would work in there; what is missing is a class to
   hang it on. */

/* Measured off window.innerWidth on the resize event, exactly as App.jsx derives
   its own isMobile — not matchMedia. Two reasons: it is the pattern already in
   this app, and matchMedia cannot be driven from outside the browser, which made
   the narrow layout untestable without a real phone. 640 rather than App's 768
   because that is where these particular rows actually collide. */
const NARROW_MAX = 640;

export function useBrowseNarrow() {
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= NARROW_MAX);

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth <= NARROW_MAX);
    onResize();                     // in case it changed before this ran
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return narrow;
}

/**
 * One row, laid out as a grid on a wide screen and as a card on a narrow one.
 *
 * @param cols    grid-template-columns, used only on wide screens
 * @param narrow  from useBrowseNarrow()
 * @param lead    how many leading children make up the card's first line.
 *                1 for a name; 2 where a number sits in front of it (the course
 *                table, whose first cell is a position rather than a label).
 *
 * The LAST child is treated as the actions cell and pinned to the right of the
 * first line, because that is what every caller puts there.
 */
/* The row's cells, with fragments opened out.
 *
 * WHY THIS EXISTS — it was every browse table on a phone, for months.
 * `L1Table` writes one row as `rowFor(r, i)`, which returns `<>{six cells}</>`.
 * A fragment makes no DOM node, so on a WIDE screen CSS grid never sees it and
 * lays the six out as six grid items: perfect, and no reason to suspect a
 * thing. The narrow path below does not lay out — it SLICES, to decide which
 * children are the title and which are the meta line — and `Children.toArray`
 * counts a fragment as ONE child. So `meta` came out empty and every cell
 * after the title stacked vertically inside the title's slot: name, summary,
 * type, price, status and destination, six lines per offer, plus a bare "—"
 * for each empty one.
 *
 * Flattening here rather than asking each caller to spread its cells: there
 * are six callers and a seventh is a fragment away from silently doing it
 * again. Recursive, because a fragment can hold a fragment.
 */
function cells(children) {
  return Children.toArray(children)
    .filter(Boolean)
    .flatMap((c) => (isValidElement(c) && c.type === Fragment ? cells(c.props.children) : [c]));
}

export function BrowseRow({ cols, narrow, lead = 1, style, children, ...rest }) {
  if (!narrow) {
    return <div style={{ ...rowBase, gridTemplateColumns: cols, ...style }} {...rest}>{children}</div>;
  }

  const items = cells(children);
  const actions = items.length > 1 ? items[items.length - 1] : null;
  const head = items.slice(0, lead);
  const meta = items.slice(lead, actions ? items.length - 1 : items.length);

  return (
    <div style={{ ...rowBase, display: "block", ...style }} {...rest}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        {head.map((child, i) => (
          /* The last of the lead cells takes the slack so a long title
             truncates against the actions button instead of shoving it off. */
          isValidElement(child) && i === head.length - 1
            ? <div key={i} style={{ flex: 1, minWidth: 0 }}>{child}</div>
            : <div key={i} style={{ flexShrink: 0, minWidth: 0 }}>{child}</div>
        ))}
        {actions && <div style={{ marginLeft: "auto", flexShrink: 0 }}>{actions}</div>}
      </div>
      {meta.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "5px 12px", marginTop: 7, minWidth: 0 }}>
          {meta}
        </div>
      )}
    </div>
  );
}

/** The bordered, clipped container a browse table sits in. */
export const browse = {
  border: "1px solid var(--border-subtle)",
  borderRadius: 12,
  overflow: "hidden",
};

/** Row layout. The caller supplies `gridTemplateColumns` to match its header. */
export const rowBase = {
  display: "grid",
  gap: 12,
  alignItems: "center",
  padding: "12px 16px",
  borderBottom: "1px solid var(--border-subtle)",
  background: "var(--bg-surface)",
};

/**
 * The uppercase header row.
 * @param cols   a grid-template-columns string, shared with the caller's rows
 * @param labels one per column; pass "" for an actions column with no heading
 * @param narrow from useBrowseNarrow() — hides the row, since column headings
 *               mean nothing once BrowseRow has stopped rendering columns
 */
export function BrowseHead({ cols, labels, narrow }) {
  if (narrow) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: cols, gap: 12, alignItems: "center", padding: "12px 16px", background: "var(--bg-sunken)", borderBottom: "1px solid var(--border-subtle)" }}>
      {labels.map((l, i) => (
        <span key={i} style={{ fontSize: 11, letterSpacing: "0.09em", textTransform: "uppercase", color: "var(--text-secondary)", fontWeight: 700 }}>{l}</span>
      ))}
    </div>
  );
}

/**
 * Status dot + label. `draft` is the fallback for an unknown status, so a new
 * status value degrades to "Draft" rather than rendering blank.
 *
 * Amber as *text* has to darken on a light canvas (#F59E0B is ~2.1:1 on white)
 * while staying amber as a fill — hence --warn-text for the word and
 * --status-draft for the dot.
 */
export function StatusPill({ status }) {
  const map = {
    draft: ["var(--warn-text)", "var(--status-draft)", "Draft — not locked yet"],
    locked: ["var(--accent)", "var(--accent)", "Locked"],
    retired: ["var(--text-muted)", "var(--border-strong)", "Retired"],
  };
  const [c, dot, label] = map[status] || map.draft;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 600, color: c, flexShrink: 0 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: dot }} />{label}
    </span>
  );
}
